import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { DecorateInteraction } from "./decorate/interaction";

// ============================================
// 타입 정의
// ============================================

/** Figma: transparent / outlined (IconButton) */
export type IconButtonVariant = "transparent" | "outlined";

/** ToggleIconButton 전용: transparent / outlined / toggle */
export type ToggleIconButtonVariant = "transparent" | "outlined" | "toggle";

/** Figma: Size = 28px / 36px */
export type IconButtonSize = 28 | 36;

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  /** 28px(기본, radius 8, icon 16) / 36px(radius 6, icon 20) */
  size?: IconButtonSize;
  icon: React.ReactNode;
  showBadge?: boolean;
}

interface ToggleIconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  variant?: ToggleIconButtonVariant;
  icon: React.ReactNode;
  toggledIcon?: React.ReactNode;
  toggled?: boolean;
  onChange?: (toggled: boolean) => void;
}

// ============================================
// Figma 디자인 토큰 (28px)
// ============================================

/** Variant 기본 스타�� */
const variantStyles: Record<IconButtonVariant, string> = {
  transparent: "bg-transparent text-label-normal",
  outlined:
    "bg-white border border-line-normal text-label-normal shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] before:-inset-px",
};

/** Variant별 DecorateInteraction 컬러 (transparent=blue, outlined=neutral) */
const interactionColor: Record<IconButtonVariant, "blue" | "neutral"> = {
  transparent: "blue",
  outlined: "neutral",
};

/** Toggle variant 스타일 */
const toggleVariantStyles: Record<ToggleIconButtonVariant, string> = {
  transparent: variantStyles.transparent,
  outlined: variantStyles.outlined,
  toggle:
    "bg-white border border-line-normal text-label-normal shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] before:-inset-px",
};

const toggleInteractionColor: Record<
  ToggleIconButtonVariant,
  "blue" | "neutral"
> = {
  transparent: "blue",
  outlined: "neutral",
  toggle: "neutral",
};

const BASE = "inline-flex items-center justify-center overflow-hidden";
/** 28px: size-7, radius-8px, icon-16px / 36px: size-9, radius-6px, icon-20px */
const sizeStyles: Record<IconButtonSize, string> = {
  28: "size-7 rounded-[8px]",
  36: "size-9 rounded-[6px]",
};
const iconWrapStyles: Record<IconButtonSize, string> = {
  28: "size-4",
  36: "size-5",
};
const DISABLED =
  "disabled:opacity-[0.4] disabled:cursor-not-allowed disabled:before:hidden";

// ============================================
// IconButton 컴포넌트
// ============================================

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = "transparent",
      size = 28,
      icon,
      showBadge = false,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <DecorateInteraction
        asChild
        variant="normal"
        color={interactionColor[variant]}
        className={cn(
          BASE,
          sizeStyles[size],
          variantStyles[variant],
          DISABLED,
          showBadge && "overflow-visible",
          className,
        )}
      >
        <button ref={ref} type="button" {...props}>
          <span
            className={cn(
              "relative flex items-center justify-center",
              iconWrapStyles[size],
            )}
          >
            {icon}
          </span>
          {showBadge && (
            <span
              className="absolute top-0 right-0 size-4 overflow-hidden flex items-center justify-center"
              aria-hidden
            >
              <div className="size-1.5 bg-sky-500 rounded-full " />
            </span>
          )}
        </button>
      </DecorateInteraction>
    );
  },
);

IconButton.displayName = "IconButton";

// ============================================
// ToggleIconButton 컴포넌트
// ============================================

export const ToggleIconButton = forwardRef<
  HTMLButtonElement,
  ToggleIconButtonProps
>(
  (
    {
      variant = "transparent",
      icon,
      toggledIcon,
      toggled = false,
      onChange,
      className,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onChange?.(!toggled);
      props.onClick?.(e);
    };

    return (
      <DecorateInteraction
        asChild
        variant="normal"
        color={toggleInteractionColor[variant]}
        className={cn(
          BASE,
          sizeStyles[28],
          toggleVariantStyles[variant],
          DISABLED,
          className,
        )}
      >
        <button
          ref={ref}
          type="button"
          data-selected={toggled}
          onClick={handleClick}
          aria-pressed={toggled ? "true" : "false"}
          {...props}
        >
          <span className="relative flex items-center justify-center size-4">
            {toggled && toggledIcon ? toggledIcon : icon}
          </span>
        </button>
      </DecorateInteraction>
    );
  },
);

ToggleIconButton.displayName = "ToggleIconButton";

export default IconButton;
